import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import type { OAuthProvider } from '../types/auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Crown } from 'lucide-react';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import {
  consumeFollowForAfterAuth,
  consumeRegisterForAfterAuth,
  consumeSubscribeForOrganizerOfAfterAuth,
} from '@/lib/registerForIntent';
import { currentSurface, postLoginPath } from '@/lib/appSurface';
import { safeRedirectOrDefault } from '@/lib/safeRedirect';
import { currentTenantOrNull } from '@/lib/tenants';
import { PublicFooter } from '@/components/PublicFooter';
import { useOrgProfile } from '@/lib/useOrgProfile';
import { useTranslation } from 'react-i18next';

export function LoginPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const { profile: orgProfile, loading: orgProfileLoading } = useOrgProfile();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setShowResend(false);
    setLoading(true);

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        if (signInError.message.toLowerCase().includes('email not confirmed')) {
          showErrorToast(t('login.errors.email_not_confirmed'));
          setShowResend(true);
          setUnverifiedEmail(email);
        } else {
          showErrorToast(t('login.errors.invalid_credentials'));
        }
        return;
      }

      const invite = searchParams.get('invite');
      if (invite) {
        navigate(`/invite/${encodeURIComponent(invite)}`, { replace: true });
        return;
      }
      const registerFor = searchParams.get('registerFor');
      if (registerFor) {
        await consumeRegisterForAfterAuth(registerFor, (path) =>
          navigate(path, { replace: true }),
        );
        return;
      }
      const followFor = searchParams.get('followFor');
      if (followFor) {
        await consumeFollowForAfterAuth(followFor, (path) =>
          navigate(path, { replace: true }),
        );
        return;
      }
      const subscribeForOrganizerOf = searchParams.get('subscribeForOrganizerOf');
      if (subscribeForOrganizerOf) {
        await consumeSubscribeForOrganizerOfAfterAuth(
          subscribeForOrganizerOf,
          (path) => navigate(path, { replace: true }),
        );
        return;
      }
      const fallback = postLoginPath();
      const redirect = safeRedirectOrDefault(
        searchParams.get('redirect'),
        fallback,
      );
      navigate(redirect, { replace: true });
    } catch {
      showErrorToast(t('login.errors.unexpected'));
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (!unverifiedEmail) return;

    const { error: resendError } = await supabase.auth.resend({
      type: 'signup',
      email: unverifiedEmail,
    });

    if (resendError) {
      // Story 075 (M4) — generic localized message, raw to console.
      console.error('Resend error', resendError);
      showErrorToast(t('login.errors.unexpected'));
      return;
    }

    showSuccessToast(t('login.resend_success'));
  }

  async function handleOAuth(provider: OAuthProvider) {
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (oauthError) {
      // Story 075 (M4) — don't surface Supabase's raw error text
      // ("PKCE flow failed", "redirect_uri does not match registered
      // URL") to end users. Log it for debugging, show a generic
      // localized message.
      console.error('OAuth error', oauthError);
      showErrorToast(t('login.errors.oauth_failed'));
    }
  }

  return (
    <div data-testid="login-page" className="flex min-h-dvh flex-col items-center bg-background px-4 py-12 pb-[max(theme(spacing.12),env(safe-area-inset-bottom))]">
      <Card className="my-auto w-full max-w-md">
        <CardHeader className="text-center">
          {/* 076 follow-up — suppress the brand badge entirely while
              the org profile is loading on white-label tenants so the
              user doesn't see the default Crown flash before it swaps
              to the uploaded logo. Reserve the vertical space with a
              same-sized placeholder so the form doesn't jump. */}
          {orgProfileLoading ? (
            <div
              data-testid="brand-loading-placeholder"
              aria-hidden="true"
              className="mx-auto mb-2 h-12 w-12"
            />
          ) : orgProfile?.logoUrl ? (
            <img
              data-testid="org-logo"
              src={orgProfile.logoUrl}
              alt={orgProfile.name}
              className="mx-auto mb-2 max-h-20 max-w-[12rem] object-contain"
            />
          ) : (
            <div
              data-testid="default-brand-badge"
              className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-accent/20"
            >
              <Crown className="h-6 w-6 text-accent-foreground" />
            </div>
          )}
          <CardTitle className="text-2xl">{t('login.title')}</CardTitle>
          <CardDescription>
            {currentSurface() === 'player'
              ? t('login.description_player')
              : t('login.description_director')}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-6">
          <form onSubmit={handleSubmit} noValidate data-testid="login-form" className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">{t('login.email_label')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder={t('login.email_placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="email-input"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="password">{t('login.password_label')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder={t('login.password_placeholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="password-input"
              />
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="self-end text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                {t('login.forgot_password_link')}
              </Link>
            </div>

            <Button type="submit" disabled={loading} data-testid="login-button" className="w-full">
              {loading ? t('login.submitting') : t('login.submit')}
            </Button>
          </form>

          {showResend && (
            <Button
              variant="outline"
              onClick={handleResend}
              data-testid="resend-verification-button"
              className="w-full"
            >
              {t('login.resend_verification')}
            </Button>
          )}

          <div className="flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground uppercase">{t('login.or_continue_with')}</span>
            <Separator className="flex-1" />
          </div>

          <div className="flex flex-col gap-3">
            <Button
              variant="outline"
              onClick={() => handleOAuth('google')}
              data-testid="google-oauth-button"
              className="w-full"
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              {t('login.google')}
            </Button>

            {import.meta.env.VITE_HIDE_APPLE_OAUTH !== 'true' && (
              <Button
                variant="outline"
                onClick={() => handleOAuth('apple')}
                data-testid="apple-oauth-button"
                className="w-full"
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                {t('login.apple')}
              </Button>
            )}
          </div>

          {/* Story 063 — hide the Register link on white-label tenants
              where self-registration is disabled. Existing players
              following an invite= link still need to reach /register
              (the page itself accepts invite tokens regardless of the
              tenant's self-reg setting), so we don't hide the link
              when an invite token is present. */}
          {((currentTenantOrNull()?.allowsSelfRegistration ?? true) ||
            !!searchParams.get('invite')) && (
            <p className="text-center text-sm text-muted-foreground">
              {t('login.no_account')}{' '}
              <Link
                to={{
                  pathname: '/register',
                  search: searchParams.get('invite')
                    ? `?invite=${encodeURIComponent(searchParams.get('invite')!)}`
                    : searchParams.get('registerFor')
                      ? `?registerFor=${encodeURIComponent(searchParams.get('registerFor')!)}`
                      : searchParams.get('followFor')
                        ? `?followFor=${encodeURIComponent(searchParams.get('followFor')!)}`
                        : searchParams.get('subscribeForOrganizerOf')
                          ? `?subscribeForOrganizerOf=${encodeURIComponent(searchParams.get('subscribeForOrganizerOf')!)}`
                          : '',
                }}
                className="text-foreground underline underline-offset-4 hover:text-foreground/80"
              >
                {t('login.register_link')}
              </Link>
            </p>
          )}
        </CardContent>
      </Card>
      <PublicFooter />
    </div>
  );
}
